import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import { authAPI } from '@/lib/auth';
import { useAuth } from '@/context/AuthContext';
import { landingPathFor } from '@/lib/landing';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { AlertCircle, Loader2, Eye, EyeOff, CheckCircle2 } from 'lucide-react';

/**
 * Parse URL hash for Supabase auth tokens
 */
function parseHashParams(hash) {
  if (!hash || hash.length < 2) return {};
  const params = new URLSearchParams(hash.substring(1));
  const result = {};
  for (const [key, value] of params.entries()) {
    result[key] = value;
  }
  return result;
}

export default function ResetPassword() {
  const navigate = useNavigate();
  const { refreshUser } = useAuth();

  const continueAfterReset = async () => {
    const profile = await refreshUser();
    navigate(profile ? landingPathFor(profile, null, '/account') : '/login', { replace: true });
  };
  
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [loading, setLoading] = useState(false);
  const [hasRecoverySession, setHasRecoverySession] = useState(false);
  const [checkingSession, setCheckingSession] = useState(true);

  // Password validation
  const passwordRequirements = [
    { met: password.length >= 8, text: 'At least 8 characters' },
    { met: /[A-Z]/.test(password), text: 'One uppercase letter' },
    { met: /[a-z]/.test(password), text: 'One lowercase letter' },
    { met: /\d/.test(password), text: 'One number' },
  ];
  
  const allRequirementsMet = passwordRequirements.every(r => r.met);
  const passwordsMatch = password === confirmPassword && confirmPassword.length > 0;

  // Check if we have a recovery session from Supabase
  useEffect(() => {
    let mounted = true;
    
    const checkRecoverySession = async () => {
      console.log('[ResetPassword] Checking for recovery session...');
      console.log('[ResetPassword] Current hash:', window.location.hash);
      
      // First, check if there's a recovery token in the URL hash
      const hashParams = parseHashParams(window.location.hash);
      const hasRecoveryToken = hashParams.type === 'recovery' && hashParams.access_token;
      
      if (hasRecoveryToken) {
        console.log('[ResetPassword] Found recovery token in URL hash');
        // Supabase will automatically process the hash and establish a session
        // We need to wait for the auth state change
      }
      
      // Subscribe to auth state changes
      const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
        console.log('[ResetPassword] Auth state change:', event, 'hasSession:', !!session);
        
        if (!mounted) return;
        
        if (event === 'PASSWORD_RECOVERY' || event === 'SIGNED_IN') {
          console.log('[ResetPassword] Recovery session established');
          setHasRecoverySession(true);
          setCheckingSession(false);
        } else if (event === 'SIGNED_OUT') {
          setHasRecoverySession(false);
        }
      });

      // Also check if we already have a session
      // Give Supabase a moment to process the hash if present
      await new Promise(resolve => setTimeout(resolve, 500));
      
      const { data: { session }, error } = await supabase.auth.getSession();
      
      if (error) {
        console.error('[ResetPassword] Error getting session:', error);
      }
      
      console.log('[ResetPassword] Current session:', session ? 'exists' : 'none');
      
      if (mounted) {
        if (session) {
          setHasRecoverySession(true);
        }
        setCheckingSession(false);
      }

      return () => {
        mounted = false;
        subscription?.unsubscribe();
      };
    };

    checkRecoverySession();
    
    return () => {
      mounted = false;
    };
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!allRequirementsMet) {
      setError('Please meet all password requirements');
      return;
    }

    if (!passwordsMatch) {
      setError('Passwords do not match');
      return;
    }

    setLoading(true);

    try {
      await authAPI.resetPassword(password);
      setSuccess(true);
    } catch (err) {
      console.error('[ResetPassword] Error:', err);
      setError(err.message || 'Failed to reset password. The link may have expired.');
    } finally {
      setLoading(false);
    }
  };

  // Show loading while checking session
  if (checkingSession) {
    return (
      <div className="min-h-screen bg-[#050816] flex items-center justify-center px-4 py-12">
        <div className="absolute inset-0 bg-gradient-to-b from-[#0f1724] to-[#050816]" />
        <div className="relative z-10 flex flex-col items-center gap-4">
          <Loader2 className="h-8 w-8 text-[#C6A14A] animate-spin" />
          <p className="text-[#E8E4DE]/60 text-sm">Verifying reset link...</p>
        </div>
      </div>
    );
  }

  // No recovery session - invalid link
  if (!hasRecoverySession) {
    return (
      <div className="min-h-screen bg-[#050816] flex items-center justify-center px-4 py-12">
        <div className="absolute inset-0 bg-gradient-to-b from-[#0f1724] to-[#050816]" />
        
        <Card className="w-full max-w-md relative z-10 bg-[#0f1724] border-white/10">
          <CardHeader className="text-center">
            <div className="mx-auto w-16 h-16 bg-red-400/20 rounded-full flex items-center justify-center mb-4">
              <AlertCircle className="h-8 w-8 text-red-400" />
            </div>
            <CardTitle className="text-2xl font-light text-white">
              Invalid Reset Link
            </CardTitle>
            <CardDescription className="text-[#E8E4DE]/60">
              This password reset link is invalid or has expired. Please request a new one.
            </CardDescription>
          </CardHeader>
          <CardFooter>
            <Button
              asChild
              className="w-full bg-[#C6A14A] text-[#050816] hover:bg-[#D4AF5A] font-medium h-11"
            >
              <Link to="/forgot-password">Request New Link</Link>
            </Button>
          </CardFooter>
        </Card>
      </div>
    );
  }

  if (success) {
    return (
      <div className="min-h-screen bg-[#050816] flex items-center justify-center px-4 py-12">
        <div className="absolute inset-0 bg-gradient-to-b from-[#0f1724] to-[#050816]" />
        
        <Card className="w-full max-w-md relative z-10 bg-[#0f1724] border-white/10">
          <CardHeader className="text-center">
            <div className="mx-auto w-16 h-16 bg-[#C6A14A]/20 rounded-full flex items-center justify-center mb-4">
              <CheckCircle2 className="h-8 w-8 text-[#C6A14A]" />
            </div>
            <CardTitle className="text-2xl font-light text-white">
              Password Reset!
            </CardTitle>
            <CardDescription className="text-[#E8E4DE]/60">
              Your password has been successfully reset. You can now sign in with your new password.
            </CardDescription>
          </CardHeader>
          <CardFooter>
            <Button
              onClick={continueAfterReset}
              className="w-full bg-[#C6A14A] text-[#050816] hover:bg-[#D4AF5A] font-medium h-11"
              data-testid="reset-password-continue-btn"
            >
              Continue
            </Button>
          </CardFooter>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#050816] flex items-center justify-center px-4 py-12">
      <div className="absolute inset-0 bg-gradient-to-b from-[#0f1724] to-[#050816]" />
      
      <Card className="w-full max-w-md relative z-10 bg-[#0f1724] border-white/10">
        <CardHeader className="space-y-1 text-center">
          <Link to="/" className="inline-block mb-4">
            <img 
              src="https://customer-assets.emergentagent.com/job_page-builder-81/artifacts/a7jf2g4n_RCSTUDIOS-logo-h240.png"
              alt="RC Studios" 
              className="h-8 mx-auto"
              style={{ filter: 'brightness(0) invert(1)' }}
            />
          </Link>
          <CardTitle className="text-2xl font-light text-white tracking-wide">
            Reset Password
          </CardTitle>
          <CardDescription className="text-[#E8E4DE]/60">
            Enter your new password below
          </CardDescription>
        </CardHeader>
        
        <form onSubmit={handleSubmit}>
          <CardContent className="space-y-4">
            {error && (
              <div className="flex items-center gap-2 p-3 text-sm text-red-400 bg-red-400/10 border border-red-400/20 rounded-lg">
                <AlertCircle className="h-4 w-4 shrink-0" />
                <span>{error}</span>
              </div>
            )}
            
            <div className="space-y-2">
              <Label htmlFor="password" className="text-[#E8E4DE]/80 text-sm">
                New Password
              </Label>
              <div className="relative">
                <Input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  placeholder="Create a strong password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  disabled={loading}
                  className="bg-[#050816]/50 border-white/10 text-white placeholder:text-white/30 focus:border-[#C6A14A] focus:ring-[#C6A14A]/20 pr-10"
                  data-testid="reset-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-white/40 hover:text-white/60 transition-colors"
                >
                  {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
              
              {/* Password requirements */}
              {password.length > 0 && (
                <div className="space-y-1 pt-2">
                  {passwordRequirements.map((req, i) => (
                    <div 
                      key={i} 
                      className={`flex items-center gap-2 text-xs ${
                        req.met ? 'text-green-400' : 'text-white/40'
                      }`}
                    >
                      <div className={`w-1.5 h-1.5 rounded-full ${
                        req.met ? 'bg-green-400' : 'bg-white/20'
                      }`} />
                      {req.text}
                    </div>
                  ))}
                </div>
              )}
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="confirmPassword" className="text-[#E8E4DE]/80 text-sm">
                Confirm New Password
              </Label>
              <Input
                id="confirmPassword"
                type={showPassword ? 'text' : 'password'}
                placeholder="Confirm your password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
                disabled={loading}
                className={`bg-[#050816]/50 border-white/10 text-white placeholder:text-white/30 focus:border-[#C6A14A] focus:ring-[#C6A14A]/20 ${
                  confirmPassword.length > 0 && !passwordsMatch ? 'border-red-400/50' : ''
                }`}
                data-testid="reset-confirm-password-input"
              />
              {confirmPassword.length > 0 && !passwordsMatch && (
                <p className="text-xs text-red-400">Passwords do not match</p>
              )}
            </div>
          </CardContent>
          
          <CardFooter>
            <Button
              type="submit"
              disabled={loading || !allRequirementsMet || !passwordsMatch}
              className="w-full bg-[#C6A14A] text-[#050816] hover:bg-[#D4AF5A] font-medium h-11 disabled:opacity-50"
              data-testid="reset-password-submit-btn"
            >
              {loading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Resetting...
                </>
              ) : (
                'Reset Password'
              )}
            </Button>
          </CardFooter>
        </form>
      </Card>
    </div>
  );
}
